'use client';

import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslations } from 'next-intl';
import toast from 'react-hot-toast';
import api from '@/axiosSetup';

const BookingPolicyContext = createContext(null);

export function isBookingPolicyError(error) {
  return error?.response?.data?.code === 'BOOKINGS_DISABLED';
}

export function BookingPolicyProvider({ children }) {
  const [status, setStatus] = useState('checking');
  const latestCheck = useRef(0);
  const t = useTranslations('BookingPolicy');

  const refreshBookingStatus = useCallback(async () => {
    const check = ++latestCheck.current;
    try {
      const response = await api.get('/api/booking-status', {
        headers: { 'Cache-Control': 'no-cache' },
        timeout: 10000,
      });
      if (typeof response.data?.bookingsEnabled !== 'boolean') {
        throw new Error('Invalid booking status response');
      }
      const enabled = response.data.bookingsEnabled;
      if (check === latestCheck.current) setStatus(enabled ? 'enabled' : 'disabled');
      return enabled;
    } catch {
      // A failed check must not allow a payment to start using stale status.
      if (check === latestCheck.current) setStatus('unavailable');
      return null;
    }
  }, []);

  const requireBookingsEnabled = useCallback(async () => {
    // Recheck at the moment of submission so an already-open page respects
    // a server switch change. The server also enforces the switch itself.
    const enabled = await refreshBookingStatus();
    if (enabled === true) return true;
    toast.error(t(enabled === false ? 'Disabled' : 'Unavailable'), { id: 'booking-policy' });
    return false;
  }, [refreshBookingStatus, t]);

  const reportBookingPolicyError = useCallback((error) => {
    if (!isBookingPolicyError(error)) return false;
    latestCheck.current++;
    setStatus('disabled');
    toast.error(t('Disabled'), { id: 'booking-policy' });
    return true;
  }, [t]);

  useEffect(() => {
    refreshBookingStatus();
    const onFocus = () => { refreshBookingStatus(); };
    window.addEventListener('focus', onFocus);
    // Keep the notice current if the server rejects a request after its
    // submission check, including calls from older booking screens.
    const interceptor = api.interceptors.response.use(response => response, error => {
      if (isBookingPolicyError(error)) {
        latestCheck.current++;
        setStatus('disabled');
      }
      return Promise.reject(error);
    });
    return () => {
      window.removeEventListener('focus', onFocus);
      api.interceptors.response.eject(interceptor);
    };
  }, [refreshBookingStatus]);

  const value = useMemo(() => ({ status, refreshBookingStatus, requireBookingsEnabled, reportBookingPolicyError }),
    [status, refreshBookingStatus, requireBookingsEnabled, reportBookingPolicyError]);

  return <BookingPolicyContext.Provider value={value}>{children}</BookingPolicyContext.Provider>;
}

export function useBookingPolicy() {
  const context = useContext(BookingPolicyContext);
  if (!context) throw new Error('useBookingPolicy must be used within BookingPolicyProvider');
  return context;
}
